import JSEncrypt from 'jsencrypt';
import CryptoJS from 'crypto-js';

class EncryptionService {
  constructor() {
    this.privateKey = null;
    this.publicKey = null;
  }

  generateKeyPair() {
    const rsaEncrypt = new JSEncrypt({ default_key_size: 2048 });
    rsaEncrypt.getKey();
    
    this.privateKey = rsaEncrypt.getPrivateKey();
    this.publicKey = rsaEncrypt.getPublicKey();
    
    return {
      privateKey: this.privateKey,
      publicKey: this.publicKey,
    };
  }

  setPrivateKey(privateKey) {
    this.privateKey = privateKey;
    if (privateKey && typeof window !== 'undefined') {
      localStorage.setItem('privateKey', privateKey);
    }
  }

  setPublicKey(publicKey) {
    this.publicKey = publicKey;
    if (publicKey && typeof window !== 'undefined') {
      localStorage.setItem('publicKey', publicKey);
    }
  }

  getPrivateKey() {
    if (this.privateKey) return this.privateKey;
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('privateKey');
      if (stored) this.privateKey = stored;
      return stored;
    }
    return null;
  }

  getPublicKey() {
    if (this.publicKey) return this.publicKey;
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('publicKey');
      if (stored) this.publicKey = stored;
      return stored;
    }
    return null;
  }

  clearKeys() {
    this.privateKey = null;
    this.publicKey = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('privateKey');
      localStorage.removeItem('publicKey');
    }
  }

  // Original hybrid encryption (backward compatibility)
  encryptWithHybrid(plaintext, recipientPublicKey) {
    try {
      // Validate public key format
      if (!recipientPublicKey.includes('-----BEGIN PUBLIC KEY-----') || 
          !recipientPublicKey.includes('-----END PUBLIC KEY-----')) {
        throw new Error('Invalid public key format');
      }

      // Generate random AES key (256-bit) and IV
      const aesKey = CryptoJS.lib.WordArray.random(32);
      const iv = CryptoJS.lib.WordArray.random(16);
      
      // Encrypt message with AES
      const encrypted = CryptoJS.AES.encrypt(plaintext, aesKey, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
      
      // Encrypt AES key with recipient's RSA public key
      const encryptRSA = new JSEncrypt();
      encryptRSA.setPublicKey(recipientPublicKey);
      
      const aesKeyBase64 = aesKey.toString(CryptoJS.enc.Base64);
      const encryptedKey = encryptRSA.encrypt(aesKeyBase64);
      
      if (!encryptedKey) {
        throw new Error('RSA encryption failed');
      }
      
      return {
        encryptedKey,
        encryptedData: encrypted.toString(),
        iv: iv.toString(CryptoJS.enc.Base64)
      };
    } catch (error) {
      console.error('Hybrid encryption error:', error);
      throw error;
    }
  }

  // Original hybrid decryption (backward compatibility)
  decryptWithHybrid(encryptedKey, encryptedData, iv, privateKey) {
    try {
      const decryptRSA = new JSEncrypt();
      decryptRSA.setPrivateKey(privateKey);
      const aesKeyBase64 = decryptRSA.decrypt(encryptedKey);
      
      if (!aesKeyBase64) {
        throw new Error('RSA decryption failed');
      }
      
      const aesKey = CryptoJS.enc.Base64.parse(aesKeyBase64);
      const ivWordArray = CryptoJS.enc.Base64.parse(iv);
      
      const decrypted = CryptoJS.AES.decrypt(encryptedData, aesKey, {
        iv: ivWordArray,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
      
      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedText) {
        throw new Error('Decryption produced empty result');
      }
      
      // Try to parse as JSON (message package)
      try {
        const parsed = JSON.parse(decryptedText);
        return parsed.content || decryptedText;
      } catch {
        return decryptedText;
      }
      
    } catch (error) {
      console.error('Hybrid decryption error:', error);
      throw error;
    }
  }

  // New: Double encryption for messages (encrypts for both sender and recipient)
prepareDoubleEncryptionMessage(content, recipientPublicKey, senderPublicKey) {
  console.log("🔵 [prepareDoubleEncryptionMessage] START");
  console.log("🔵 Content length:", content?.length);
  console.log("🔵 Recipient public key exists:", !!recipientPublicKey);
  console.log("🔵 Sender public key exists:", !!senderPublicKey);
  
  try {
    // Validate inputs
    if (!recipientPublicKey) {
      throw new Error('Recipient public key is required');
    }
    if (!senderPublicKey) {
      throw new Error('Sender public key is required');
    }
    if (!content) {
      throw new Error('Message content is required');
    }
    
    // Generate random AES key (256-bit) and IV
    console.log("🟢 Generating AES key and IV...");
    const aesKey = CryptoJS.lib.WordArray.random(32);
    const iv = CryptoJS.lib.WordArray.random(16);
    console.log("🟢 AES key generated, length:", aesKey.sigBytes);
    console.log("🟢 IV generated, length:", iv.sigBytes);
    
    // Prepare message package with metadata
    const messagePackage = JSON.stringify({
      content: content,
      timestamp: Date.now()
    });
    console.log("🟢 Message package prepared, length:", messagePackage.length);
    
    // Encrypt message with AES
    console.log("🟢 Encrypting with AES...");
    const encrypted = CryptoJS.AES.encrypt(messagePackage, aesKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    console.log("🟢 AES encryption complete, encrypted length:", encrypted.toString().length);
    
    const aesKeyBase64 = aesKey.toString(CryptoJS.enc.Base64);
    console.log("🟢 AES key converted to Base64, length:", aesKeyBase64.length);
    
    // Encrypt AES key with recipient's RSA public key
    console.log("🟢 Encrypting for recipient...");
    const encryptRSAForRecipient = new JSEncrypt();
    const recipientKeySet = encryptRSAForRecipient.setPublicKey(recipientPublicKey);
    console.log("🟢 Recipient public key set successfully:", recipientKeySet);
    
    const recipientEncryptedKey = encryptRSAForRecipient.encrypt(aesKeyBase64);
    console.log("🟢 Recipient encryption complete, result exists:", !!recipientEncryptedKey);
    if (!recipientEncryptedKey) {
      console.error("🔴 Recipient RSA encryption returned null/false");
      throw new Error('RSA encryption failed for recipient');
    }
    
    // Encrypt AES key with sender's RSA public key
    console.log("🟢 Encrypting for sender...");
    const encryptRSAForSender = new JSEncrypt();
    const senderKeySet = encryptRSAForSender.setPublicKey(senderPublicKey);
    console.log("🟢 Sender public key set successfully:", senderKeySet);
    
    const senderEncryptedKey = encryptRSAForSender.encrypt(aesKeyBase64);
    console.log("🟢 Sender encryption complete, result exists:", !!senderEncryptedKey);
    if (!senderEncryptedKey) {
      console.error("🔴 Sender RSA encryption returned null/false");
      throw new Error('RSA encryption failed for sender');
    }
    
    const result = {
      recipientEncryptedKey,
      senderEncryptedKey,
      encryptedData: encrypted.toString(),
      iv: iv.toString(CryptoJS.enc.Base64)
    };
    
    console.log("🟢 [prepareDoubleEncryptionMessage] SUCCESS");
    console.log("🔵 Result summary:", {
      recipientKeyLength: result.recipientEncryptedKey.length,
      senderKeyLength: result.senderEncryptedKey.length,
      dataLength: result.encryptedData.length,
      ivLength: result.iv.length
    });
    
    return result;
  } catch (error) {
    console.error("🔴 [prepareDoubleEncryptionMessage] ERROR:", error);
    throw error;
  }
}

  // New: Decrypt double encrypted message
  decryptDoubleEncryptionMessage(encryptedData, iv, encryptedKey, privateKey) {
    try {
      const decryptRSA = new JSEncrypt();
      decryptRSA.setPrivateKey(privateKey);
      const aesKeyBase64 = decryptRSA.decrypt(encryptedKey);
      
      if (!aesKeyBase64) {
        throw new Error('RSA decryption failed - wrong private key?');
      }
      
      const aesKey = CryptoJS.enc.Base64.parse(aesKeyBase64);
      const ivWordArray = CryptoJS.enc.Base64.parse(iv);
      
      const decrypted = CryptoJS.AES.decrypt(encryptedData, aesKey, {
        iv: ivWordArray,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
      
      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedText) {
        throw new Error('Decryption produced empty result');
      }
      
      // Try to parse as JSON (message package)
      try {
        const parsed = JSON.parse(decryptedText);
        return parsed.content || decryptedText;
      } catch {
        return decryptedText;
      }
      
    } catch (error) {
      console.error('Double encryption decryption error:', error);
      throw error;
    }
  }

  // Universal decrypt method that handles both single and double encryption
  decryptMessage(message, privateKey, isOwn = false) {
    if (!privateKey) {
      console.error('No private key provided for decryption');
      return null;
    }
    
    // Check if using double encryption (has senderEncryptedKey or recipientEncryptedKey)
    if (message.senderEncryptedKey || message.recipientEncryptedKey) {
      // Select the appropriate key based on who owns the message
      let encryptedKey;
      if (isOwn) {
        encryptedKey = message.senderEncryptedKey;
        if (!encryptedKey) {
          console.error('No sender encrypted key found for own message');
          return null;
        }
      } else {
        encryptedKey = message.recipientEncryptedKey;
        if (!encryptedKey) {
          console.error('No recipient encrypted key found for received message');
          return null;
        }
      }
      
      return this.decryptDoubleEncryptionMessage(
        message.encryptedData,
        message.iv,
        encryptedKey,
        privateKey
      );
    }
    
    // Fallback to single encryption (backward compatibility)
    if (!message.encryptedKey || !message.encryptedData || !message.iv) {
      console.error('Message missing required encryption fields');
      return null;
    }
    
    try {
      return this.decryptWithHybrid(
        message.encryptedKey,
        message.encryptedData,
        message.iv,
        privateKey
      );
    } catch (error) {
      console.error('Message decryption error:', error);
      return null;
    }
  }

  // Prepare message package for sending (legacy method)
  prepareMessagePackage(content, sender) {
    return JSON.stringify({
      content,
      timestamp: Date.now(),
      sender
    });
  }
}

export default new EncryptionService();
